/**
 * useSubscription - Returns the current user's subscription plan and feature flags.
 *
 * Fetches the plan from the server once per session. Falls back to 'free'
 * if unauthenticated or if the request fails.
 */

import { useState, useEffect } from "react";
import { useSession } from "./useSession";
import type { SubscriptionPlan } from "@bash/shared";
import { hasMinimumPlan } from "@bash/shared";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

/**
 * Every mounted consumer refetches on this event — the page's card and the
 * sidebar badge read the same endpoint through their own mounts, and before
 * this signal existed the only thing keeping them agreeing after a purchase
 * was a full `window.location.reload()`. The checkout celebration replaced
 * that reload, so the refresh has to travel by signal instead.
 */
const REFRESH_EVENT = "vestra:subscription-refresh";

export function refreshSubscription(): void {
  window.dispatchEvent(new Event(REFRESH_EVENT));
}

export interface UseSubscriptionResult {
  /** The user's current plan */
  plan: SubscriptionPlan;
  /** Whether the plan data has loaded */
  isLoaded: boolean;

  // Convenience flags
  isPro: boolean;
  isMax: boolean;
  isUltra: boolean;
  isCustom: boolean;

  // Feature gates
  canUseAutopilot: boolean;
}

export function useSubscription(): UseSubscriptionResult {
  const { session } = useSession();
  const [plan, setPlan] = useState<SubscriptionPlan>("free");
  const [isLoaded, setIsLoaded] = useState(false);
  // Bumped by the refresh event; a dependency of the fetch effect below.
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const bump = () => setRefreshTick((t) => t + 1);
    window.addEventListener(REFRESH_EVENT, bump);
    return () => window.removeEventListener(REFRESH_EVENT, bump);
  }, []);

  useEffect(() => {
    if (!session?.access_token) {
      // Not logged in — default to free, mark as loaded
      setPlan("free");
      setIsLoaded(true);
      return;
    }

    let cancelled = false;

    fetch(`${SERVER_URL}/api/account/subscription`, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ plan: SubscriptionPlan }>;
      })
      .then((data) => {
        if (!cancelled) {
          setPlan(data.plan ?? "free");
          setIsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Fail open to free — never block the user
          setPlan("free");
          setIsLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session?.access_token, refreshTick]);

  return {
    plan,
    isLoaded,
    isPro: hasMinimumPlan(plan, "pro"),
    isMax: hasMinimumPlan(plan, "max"),
    isUltra: hasMinimumPlan(plan, "ultra"),
    isCustom: hasMinimumPlan(plan, "custom"),
    canUseAutopilot: hasMinimumPlan(plan, "pro"),
  };
}
